import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Expand } from 'lucide-react';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';

function resolveFotoSrc(foto) {
  if (!foto || typeof foto !== 'object') return '';
  if (foto.url != null && String(foto.url).trim()) return String(foto.url).trim();
  if (foto.base64 != null && String(foto.base64).trim()) {
    const mime = foto.mimeType || 'image/jpeg';
    return `data:${mime};base64,${foto.base64}`;
  }
  return '';
}

/** URL com fragmento de tempo para o browser mostrar o 1º frame no thumb. */
function resolveVideoThumbSrc(src) {
  const s = String(src || '').trim();
  if (!s || s.startsWith('data:')) return s;
  if (/#t=/i.test(s)) return s;
  return `${s}#t=0.1`;
}

function isFotoVideo(foto) {
  if (!foto || typeof foto !== 'object') return false;
  if (foto.isVideo === true) return true;
  return String(foto.mimeType || '')
    .toLowerCase()
    .startsWith('video/');
}

/**
 * Modal de fotos da OS — mesmo comportamento visual do CheckoutView (thumbs + zoom).
 */
export default function TechnicianPanelOsFotosModal({
  open,
  onClose,
  numeroOs,
  equip,
  serial = '—',
  fotos = [],
}) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [zoomFoto, setZoomFoto] = useState(null);

  useEffect(() => {
    if (!open) {
      setSelectedIdx(0);
      setZoomFoto(null);
      return undefined;
    }
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (zoomFoto) {
        setZoomFoto(null);
        return;
      }
      onClose?.();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose, zoomFoto]);

  useEffect(() => {
    if (!open) return;
    setSelectedIdx(0);
  }, [open, numeroOs]);

  if (!open) return null;

  const current = fotos[selectedIdx] || null;
  const currentSrc = resolveFotoSrc(current);
  const currentVideo = isFotoVideo(current);

  return createPortal(
    <>
      <div className="tpm-os-fotos-overlay" role="presentation" onClick={onClose}>
        <div
          className="tpm-os-fotos-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`Fotos da OS ${numeroOs || ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="tpm-os-fotos-header">
            <div className="tpm-os-fotos-header-info">
              <h3 className="tpm-os-fotos-title">Fotos da OS {numeroOs || '—'}</h3>
              <div className="tpm-os-fotos-subtitle">
                <span>{equip || '—'}</span>
                <span>•</span>
                <span>Série: {serial || '—'}</span>
              </div>
            </div>
            <button type="button" className="tpm-os-fotos-close" aria-label="Fechar" onClick={onClose}>
              ✕
            </button>
          </div>
          <div className="tpm-os-fotos-body">
            {fotos.length === 0 ? (
              <div className="tpm-os-fotos-empty">Nenhuma foto encontrada para esta OS.</div>
            ) : (
              <div className="tpm-os-fotos-layout">
                <div className="tpm-os-fotos-thumbs">
                  {fotos.map((foto, idx) => {
                    const src = resolveFotoSrc(foto);
                    const video = isFotoVideo(foto);
                    return (
                      <GoHoverTooltip
                        key={idx}
                        text={foto.operacao || (video ? `Vídeo ${idx + 1}` : `Foto ${idx + 1}`)}
                        align="center"
                        className="go-hover-tip-wrap--contents"
                      >
                        <button
                          type="button"
                          className={`tpm-os-fotos-thumb${idx === selectedIdx ? ' is-active' : ''}${
                            video ? ' tpm-os-fotos-thumb--video' : ''
                          }`}
                          onClick={() => setSelectedIdx(idx)}
                          aria-label={foto.operacao || (video ? `Vídeo ${idx + 1}` : `Foto ${idx + 1}`)}
                        >
                          {video ? (
                            <>
                              <video
                                className="tpm-os-fotos-thumb-preview"
                                src={resolveVideoThumbSrc(src)}
                                muted
                                playsInline
                                preload="metadata"
                                aria-hidden
                              />
                              <span className="tpm-os-fotos-thumb-video" aria-hidden>
                                <span className="tpm-os-fotos-thumb-video-icon">▶</span>
                                <span className="tpm-os-fotos-thumb-video-label">Vídeo</span>
                              </span>
                            </>
                          ) : (
                            <img src={src} alt={foto.operacao || `Foto ${idx + 1}`} />
                          )}
                        </button>
                      </GoHoverTooltip>
                    );
                  })}
                </div>
                <div className="tpm-os-fotos-main">
                  {current && currentSrc && (
                    <figure className="tpm-os-fotos-item">
                      <div className="tpm-os-fotos-wrapper">
                        {currentVideo ? (
                          <video
                            key={currentSrc}
                            className="tpm-os-fotos-img tpm-os-fotos-video"
                            src={currentSrc}
                            controls
                            playsInline
                            preload="metadata"
                            controlsList="nodownload"
                          />
                        ) : (
                          <>
                            <img
                              src={currentSrc}
                              alt={`Foto ${selectedIdx + 1} da OS ${numeroOs || ''}`}
                              className="tpm-os-fotos-img"
                            />
                            <GoHoverTooltip text="Ampliar foto" align="center" className="go-hover-tip-wrap--contents">
                              <button
                                type="button"
                                className="tpm-os-fotos-zoom-btn"
                                aria-label="Ampliar foto"
                                onClick={() => setZoomFoto(current)}
                              >
                                <Expand size={14} strokeWidth={2} aria-hidden />
                              </button>
                            </GoHoverTooltip>
                          </>
                        )}
                      </div>
                      {current.operacao && (
                        <figcaption className="tpm-os-fotos-caption">{current.operacao}</figcaption>
                      )}
                    </figure>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {zoomFoto && !isFotoVideo(zoomFoto) && (
        <div className="tpm-os-fotos-overlay tpm-os-fotos-overlay--zoom" role="presentation" onClick={() => setZoomFoto(null)}>
          <div className="tpm-os-fotos-zoom-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="tpm-os-fotos-close" aria-label="Fechar" onClick={() => setZoomFoto(null)}>
              ✕
            </button>
            <div className="tpm-os-fotos-zoom-body">
              <img
                src={resolveFotoSrc(zoomFoto)}
                alt={`Foto ampliada da OS ${numeroOs || ''}`}
                className="tpm-os-fotos-zoom-img"
              />
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}
